import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card, Form, Row, Col, Button, Alert, FormGroup, FormLabel, FormControl } from 'react-bootstrap';
import { getExportJobs, getAllJobs } from '../../Redux/Actions/jobs';
import DataTable from "../../utils/dataTable";
import DropDown from "../Ui/DropDown";
import { toast } from 'react-toastify';
import moment from 'moment';
import * as userActions from '../../Redux/Actions/user';
import { useTranslation } from 'react-i18next';

export const ConsolidatedReport = ({ history }) => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [jobs, setJobs] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [jobStatus, setJobStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const userData = useSelector(({ User }) => User.data);

  const fetchJobs = async (page = 0) => {
    if (!startDate || !endDate) return;

    setLoading(true);
    try {
      const response = await getAllJobs({
        page,
        startDate,
        endDate,
        companyName: '',
        departmentId: '',
        masterProcessId: '',
        processId: '',
        id: '',
        jobStatus: jobStatus?.value || ''
      });

      if (response && response.data && response.data.data) {
        setJobs(response.data.data.companyJobs || []);
        setTotalCount(response.data.data.totalCount || 0);
      }
    } catch (err) {
      console.error(err);
      setError(t('reports.consolidatedReport.errorFetch'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (startDate && endDate) {
      fetchJobs(currentPage);
    }
  }, [currentPage]);

  const handleSearch = () => {
    setCurrentPage(0);
    fetchJobs(0);
  };

  const handleExport = async () => {
    if (!startDate || !endDate) {
      setError(t('reports.consolidatedReport.errorSelectDates'));
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const response = await getExportJobs(startDate, endDate, jobStatus?.value || '');

      if (response) {
        const url = window.URL.createObjectURL(new Blob([response]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `jobs-report-${moment().format('YYYY-MM-DD')}.csv`);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);

        setSuccess(t('reports.consolidatedReport.successDownload'));
      }
    } catch (err) {
      setError(t('reports.consolidatedReport.errorDownload'));
      console.error('Export error:', err);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      Header: t("eidBasmati.id"),
      accessor: "id",
    },
    {
      Header: t("eidBasmati.corporateJobReference"),
      accessor: "corporateJobReference",
    },
    {
      Header: t("eidBasmati.poNumber"),
      accessor: "poNumber",
    },
    {
      Header: t("eidBasmati.companyName"),
      accessor: "companyName"
    },
    {
      Header: t('eidBasmati.jobInfo'),
      accessor: "departmentName",
      Cell: ({ row }) => (
        <div>
          <strong>D</strong> <strong>:</strong> {row.original.departmentName} <br />
          <strong>M</strong> <strong>:</strong> {row.original.masterProcessName}<br />
          <strong>P</strong> <strong>:</strong> {row.original.processName}<br />
          <strong>Job count</strong> <strong>:</strong> {row.original.jobCount}
        </div>
      )
    },
    {
      Header: t("eidBasmati.payment"),
      accessor: "paymentTypeValue",
      Cell: ({ row }) => (
        <div>
          <strong>Type</strong> <strong>:</strong> {row.original.paymentTypeValue} <br />
          <strong>Status</strong> <strong>:</strong> {row.original.paymentStatus}<br />
          <strong>Payment Date</strong> <strong>:</strong> {row.original.paymentCompletedDate}<br />
          <strong>Due Date</strong> <strong>:</strong> {row.original.dueDate}
        </div>
      )
    },
    {
      Header: t("eidBasmati.jobStatus"),
      accessor: "status",
    }
  ];

  return (
    <div className="page-content">
      <div className="container-fluid">
        <h4 className="mb-4 fw-semibold text-dark">{t('reports.consolidatedReport.title')}</h4>

        <Card className="border-0 ">
          <Card.Body className="p-2">
            {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
            {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

            <Form>
              {/* Filter Section */}
              
              <Row className="g-3 align-items-end m-3">
                 <Col md={3} lg={3} className=''>
                  <DropDown
                    isSearchable={true}
                    label={t("eidBasmati.jobStatus")}
                    style={{ margin: "auto" }}
                    placeholder={t("eidBasmati.selectJobStatus")}
                    options={[
                      { label: "Select Status", value: "" },
                      { label: "Awaiting Payment", value: "Awaiting Payment" },
                      { label: "Pending", value: "Pending" },
                      { label: "Completed", value: "Completed" },
                      { label: "Draft", value: "Draft" },
                      { label: "Cancelled", value: "Cancelled" },
                    ]}
                    value={jobStatus}
                    onChange={(value) => setJobStatus(value)}
                    disabled={loading}
                  />
                </Col>
                <Col md={2} lg={2}>
                  <Form.Group controlId="startDate" >
                    <Form.Label className="fw-medium mb-1">{t('reports.consolidatedReport.startDate')}</Form.Label>
                    <Form.Control
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      disabled={loading}
                      max={endDate || moment().format('YYYY-MM-DD')}
                    />
                  </Form.Group>
                </Col>

                <Col md={2} lg={2}>
                  <Form.Group controlId="endDate">
                    <Form.Label className="fw-medium mb-1">{t('reports.consolidatedReport.endDate')}</Form.Label>
                    <Form.Control
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      disabled={loading}
                      min={startDate}
                      max={moment().format('YYYY-MM-DD')}
                    />
                  </Form.Group>
                </Col>

               

                <Col md={2} lg={2}>
                  <Button
                    variant="primary"
                    onClick={handleSearch}
                    disabled={loading || !startDate || !endDate}
                    className="w-100"
                  >
                    {loading ? "Searching..." : "Search"}
                  </Button>
                </Col>

                <Col md={3} lg={3}>
                  <Button
                    variant="success"
                    onClick={handleExport}
                    disabled={loading || !startDate || !endDate || jobs.length === 0}
                    className="w-100"
                  >
                    {loading ? t('reports.consolidatedReport.processing') : t('reports.consolidatedReport.downloadButton')}
                  </Button>
                </Col>
              </Row>
              
            </Form>

            <hr className="my-1 m-3" />
            <DataTable
              columns={columns}
              currentPage={currentPage}
              data={jobs}
              setCurrentPage={setCurrentPage}
              totalCount={totalCount}
            />
          </Card.Body>
        </Card>
      </div>
    </div>
  );
};

export default ConsolidatedReport;